import React, { type JSX } from "react";
import { useAppSelector, useAppDispatch } from "./hooks/reduxHooks";
import { selectSelectedExpert } from "./ExpertsSlice_Store";
import { openDealDetail, fetchDealDetail } from "./DealsTableSlice_Store";
import { Field } from "./DealDetailsPanel_Component";
import type { Expert, ExpertDeal } from "./types/redux";

// ── Helpers ────────────────────────────────────────────────────

function present<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined && (value as unknown) !== "";
}

function subtitle(expert: Expert): string {
  return [expert.jobTitle, expert.team, expert.businessUnit, expert.location]
    .filter(present)
    .join(" · ");
}

function formatRoles(roles: Record<string, number>): string {
  return Object.entries(roles)
    .map(([role, count]) => `${role} (${count})`)
    .join(", ");
}

// ── Sections ───────────────────────────────────────────────────

function ActiveBadge({ active }: { active: boolean | null }): JSX.Element | null {
  if (active === null) return null;
  const tone = active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600";
  return (
    <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-normal ${tone}`}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function ExpertStats({ expert }: { expert: Expert }): JSX.Element {
  const roles = formatRoles(expert.rolesSummary);
  return (
    <dl className="grid grid-cols-1 desktop:grid-cols-2 gap-x-6 gap-y-3">
      {present(expert.score) && <Field label="Score">{Math.round(expert.score)}</Field>}
      <Field label="Matched deals">{expert.matchedDealCount}</Field>
      {present(expert.totalDealCount) && (
        <Field label="Total CVC deals">{expert.totalDealCount}</Field>
      )}
      {present(expert.yearsOnCvcDeals) && (
        <Field label="Years on CVC deals">{expert.yearsOnCvcDeals}</Field>
      )}
      {present(expert.lastActiveDate) && <Field label="Last active">{expert.lastActiveDate}</Field>}
      {roles && <Field label="Roles">{roles}</Field>}
    </dl>
  );
}

function TopDealsTable({
  deals,
  onDealClick,
}: {
  deals: ExpertDeal[];
  onDealClick: (dealId: string) => void;
}): JSX.Element {
  return (
    <table className="min-w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200">
          {["Deal", "Role", "Sector", "Country", "Year", "Fund"].map((h) => (
            <th key={h} className="text-left py-1 pr-4 font-medium text-gray-600">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {deals.map((deal, idx) => (
          <tr key={deal.dealId ?? idx} className="border-b border-gray-100 last:border-0">
            <td className="py-1 pr-4">
              {deal.dealId ? (
                <button
                  type="button"
                  onClick={() => onDealClick(deal.dealId)}
                  className="text-cvc-blue hover:underline text-left"
                >
                  {deal.dealName || deal.dealId}
                </button>
              ) : (
                (deal.dealName ?? "--")
              )}
            </td>
            <td className="py-1 pr-4">{deal.role || "--"}</td>
            <td className="py-1 pr-4">{deal.sector || "--"}</td>
            <td className="py-1 pr-4">{deal.country || "--"}</td>
            <td className="py-1 pr-4">{deal.investmentYear ?? "--"}</td>
            <td className="py-1">{deal.fund || "--"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Main component ─────────────────────────────────────────────

export default function ExpertProfilePanel(): JSX.Element | null {
  const dispatch = useAppDispatch();
  const expert = useAppSelector(selectSelectedExpert);

  if (!expert) return null;

  const handleDealClick = (dealId: string): void => {
    dispatch(openDealDetail(dealId));
    void dispatch(fetchDealDetail(dealId));
  };

  const sub = subtitle(expert);
  const { topDeals } = expert;

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 shrink-0">
        <h2 className="text-base desktop:text-lg font-bold text-gray-800 truncate">
          {expert.personDisplayName}
          <ActiveBadge active={expert.isActiveEmployee} />
        </h2>
        {sub && <p className="mt-1 text-sm text-gray-600">{sub}</p>}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        <ExpertStats expert={expert} />

        {expert.whyMatched && (
          <>
            <div className="border-t border-gray-200" />
            <dl>
              <Field label="Why matched">
                <span className="whitespace-pre-wrap leading-relaxed text-gray-700">
                  {expert.whyMatched}
                </span>
              </Field>
            </dl>
          </>
        )}

        {topDeals.length > 0 && (
          <>
            <div className="border-t border-gray-200" />
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                Top deals
              </h3>
              <TopDealsTable deals={topDeals} onDealClick={handleDealClick} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
